<template>
  <div class="c-app p-2"
       :class="{ 'c-dark-theme': $store.state.darkMode }">
    <CCard class="w-100 mb-0">
      <CCardHeader class="py-2 d-flex justify-content-between align-items-center">
        <span>
          <BBadge variant="info"> {{ area? area.name : '' }} </BBadge>
          <b class="ml-1">지역 대시보드</b>
          <span class="text-muted ml-2">{{ area? area.description : '' }}</span>
        </span>
        <span class="d-flex align-items-center">
          <BFormSelect size="sm"
                       class="mr-2 w-auto theme-select"
                       v-model="tankVolUnit"
                       :options="tankVolUnitOpts"/>
          <BButton size="sm" variant="outline-secondary" @click="closeWindow">
            <BIconXLg/> <b>닫기</b>
          </BButton>
        </span>
      </CCardHeader>

      <CCardBody>
        <BRow v-if="isReady && area && area.tanks">
          <!-- 칼럼을 고정 분할(xl=4)하지 않고 카드 폭(520~580px)만큼 차지하게 해,
               창을 줄이면 카드가 겹치지 않고 줄바꿈되고 넓히면 한 줄에 더 들어가게 한다 (Dashboard 와 동일) -->
          <BCol v-for="tank in area.tanks" :key="tank.tid" v-show="tank.display"
                style="flex: 1 1 520px; max-width: 580px; min-width: 520px">

            <CCard style="min-width:520px; max-width:580px">
              <CCardHeader>
                <BButtonGroup size="sm" class="small">
                  <BButton variant="outline-info"
                           size="sm"
                           @click="gotoMonitor(area.arCode, tank.tid)">
                    <BIconBack/> <B>{{tank.name}}</B>
                  </BButton>
                  <BButton class="ml-1" :style="{color: '#000000', backgroundColor: oilColors[tank.oilCode]}">
                    <strong>{{oilCodeMap[tank.oilCode]}}</strong>
                  </BButton>
                  <BButton class="ml-1" :variant="tankState[tank.tid].variant">
                    <strong>{{tankState[tank.tid].text}}</strong>
                  </BButton>
                  <!-- 패킷 상태: 텍스트 대신 아이콘 배지. 상태명은 툴팁으로 보여준다 -->
                  <BBadge class="ml-1 px-2 d-inline-flex align-items-center psts-badge"
                          :variant="tankState[tank.tid].pstsVariant"
                          :class="{blink: tankState[tank.tid].psts.code!=='0000'}"
                          v-b-tooltip.hover
                          :title="tankState[tank.tid].psts.message">
                    <BIcon :icon="pstsIcon(tankState[tank.tid].psts.code)" font-scale="1.2"/>
                  </BBadge>
                </BButtonGroup>

                <div class="card-header-actions">
                  <WidgetEvents :ref="`evt-${tank.tid}`" class="float-right"/>
                </div>
              </CCardHeader>

              <CCardBody>
                <BRow class="mb-1" :key="progressKey">
                  <BCol cols="7">
                    <BProgress :max="100" height="2rem" show-value>
                      <BProgressBar :value="volumes[tank.tid].tvp"
                                    :animated="tankState[tank.tid].sts!=='N'"
                                    :variant="progressVariant(volumes[tank.tid].tvp)"/>
                    </BProgress>
                  </BCol>
                  <BCol cols="5" class="text-right">
                    <BButtonGroup size="sm">
                      <BButton variant="info">
                        <strong>{{ trans(volumes[tank.tid].ovm, 'v', tankVolUnit || tank.unitOfVol ) }}</strong>
                      </BButton>
                      <BButton variant="outline-info" class="small text-nowrap">
                        {{ trans(volumes[tank.tid].real, 'v', tankVolUnit || tank.unitOfVol) }} <BBadge>{{unitSign(tankVolUnit || tank.unitOfVol)}}</BBadge>
                      </BButton>
                    </BButtonGroup>
                  </BCol>
                </BRow>

                <BRow>
                  <BCol>
                    <TankLineChart :ref='`chart-${tank.tid}`' style="min-height: 250px; max-height: 300px;"
                                   :tank="tank"
                                   :oilColor="oilColors[tank.oilCode]"
                    />
                  </BCol>
                </BRow>
              </CCardBody>
            </CCard>

          </BCol>
        </BRow>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import '@/common/HelperMixin';
import axios from 'axios';
import WidgetEvents from './components/widget/WidgetEvents'
import TankLineChart from './components/charts/TankLineChart'
import {
  apiCall, random, sleep, trans, unitSign
} from '@/common/utils';
import {IoStsMap, PacketStsIconMap} from "@/common/constants";

export default {
  name: 'DashboardAreaPopup',
  components: {
    WidgetEvents,
    TankLineChart
  },
  data () {
    return {
      isReady: false,
      progressKey: random(0,10000),
      area: null,
      oilColors: {},
      oilCodeMap: {},
      tankState: {},
      volumes: {},
      events: {},
      socket: null,
      // 대시보드와 같은 키를 공유해 단위 선택이 함께 유지된다
      tankVolUnit: localStorage.getItem('tankVolUnit') || '',
      tankVolUnitOpts: [
        { value: '', text: '탱크단위' },
        { value: 'k', text: 'K리터(kL)' },
        { value: 'l', text: '리터(L)' },
        { value: 'g', text: '갤런(gal)' },
        { value: 'd', text: '드럼(D)' },
      ],
      trans: trans,
      unitSign: unitSign,
    }
  },

  watch: {
    tankVolUnit(newVal) {
      localStorage.setItem('tankVolUnit', newVal);
    },
  },

  async created(){
    try{
      // 팝업 창은 별도 앱 인스턴스라 부모 창이 넘겨준 인증정보로 로그인 상태를 만든다
      // (AlarmBoardPopup/모니터 팝업과 같은 방식)
      if( !window.storedUser ){
        alert("[WARN] 인증 정보 없음. 재접속 요망");
        window.close();
        return;
      }

      const storedUser = JSON.parse(window.storedUser);
      axios.defaults.headers.common['x-access-token'] = storedUser.accessToken;
      await this.$store.commit('LOGIN', storedUser);

    }catch(err){
      console.error(err);
      alert(err.message);
      window.close();
    }
  },

  async mounted(){
    try{
      await sleep(1500);

      this.oilColors = this.$store.state.codeMaps['OIL_COLOR'];
      this.oilCodeMap = this.$store.state.codeMaps['OIL_CODE'];

      const arCode = this.$route.query.arCode;
      const areaCodes = this.$store.state.area['codes'] || {};
      this.area = Object.values(areaCodes).find( a => a.arCode === arCode );

      if(!this.area){
        alert(`[WARN] 지역 정보 없음 (${arCode})`);
        window.close();
        return;
      }

      for( const tank of this.area.tanks ){
        const tid = tank.tid;
        this.volumes[tid] = { dt: null, max: 0, real: 0, ovm: 0, ovc: 0, wv: 0, tvp: 0, avgTm: 0};
        this.tankState[tid] = {
          text:'미확인',
          variant: 'dark',
          pstsVariant: 'dark',
          iosts: '',
          tsts: { code:'0000', message:'', updatedAt: null },
          psts: { code:'0000', message:'', updatedAt: null }
        };
        this.events[tid] = { events: [], dt: new Date() };
      }

      this.isReady = true;
      await sleep(500);

      this.socket = this.$store.state.socket;
      if( this.socket ){
        this.socket.removeListener('tank-sts', this.tankStateHandler);
        this.socket.removeListener('event', this.eventAlarmHandler);
        this.socket.removeListener('inventory', this.inventoryHandler);
        this.socket.on('tank-sts', this.tankStateHandler);
        this.socket.on('event', this.eventAlarmHandler);
        this.socket.on('inventory', this.inventoryHandler);
      }else{
        console.warn('[DashboardAreaPopup] socket not found');
      }

      await this.getTankChart();

    }catch(err){
      console.error(err);
    }
  },

  beforeDestroy(){
    if(this.socket) {
      this.socket.removeListener('tank-sts', this.tankStateHandler);
      this.socket.removeListener('event', this.eventAlarmHandler);
      this.socket.removeListener('inventory', this.inventoryHandler);
    }
  },

  methods: {
    // 패킷 상태 코드 → 헤더 배지 아이콘 이름
    pstsIcon(code){
      return PacketStsIconMap[code] || PacketStsIconMap[''];
    },

    closeWindow(){
      window.close();
    },

    progressVariant(v){
      if(!v) return 'dark';

      if(v<40) return 'warning';
      else if(v > 90) return 'danger';
      else return 'info';
    },

    gotoMonitor(arCode, tankId='') {
      let tid = tankId? tankId: '';
      let mode = tid? 'tank':'area';

      let query = `mode=${mode}&arCode=${arCode}&tid=${tid}`;
      let width=930;
      let winName = arCode;
      if(tid) {
        width = 514;
        winName = tid;
      }

      let route = this.$router.resolve({ path: `/monitoring?${query}`});
      const features = `left=0,top=0,width=${width},height=545,status=no,titlebar=no,location=no,menubar=no,toolbar=no`
      const newWin = window.open(route.href, `monitor_${winName}`, features );
      newWin.storedUser = window.storedUser;
    },

    /**
     * 소켓 탱크상태 수신 → 이 지역 탱크만 갱신한다.
     * 음성 안내는 본 창(Dashboard)이 담당하므로 팝업에서는 울리지 않는다.
     */
    tankStateHandler(data){
      const {tid} = data;
      const tState = this.tankState[tid];
      if(!tState) return false; // 이 지역 탱크가 아니면 무시

      this.volumes[tid] = data.payload.volume;

      const {sts} = data;
      const {psts} = data.payload;

      tState.tsts = sts;
      tState.psts = psts;
      tState.sts = sts.io;

      tState.text = IoStsMap[sts.io];

      switch (sts.io) {
        case 'I':
          tState.variant = 'danger';
          break;
        case 'O':
          tState.variant = 'info';
          break;
        case 'N':
          tState.variant = 'success';
          break;
        case 'R':
          tState.variant = 'danger';
          break;
        case 'D':
          tState.variant = 'warning';
          break;
        default:
          tState.text = '미확인';
          tState.variant = 'secondary';
          break;
      }

      switch(psts.code){
        case 'PKT_LST':
          tState.pstsVariant = 'danger';
          break;
        case 'PKT_DN':
          tState.pstsVariant = 'warning';
          break;
        case 'NO_PKT':
          tState.pstsVariant = 'dark';
          break;
        case 'READY':
          tState.pstsVariant = 'info';
          break;
        case '0000':
          tState.pstsVariant = 'success';
          break
        default:
          tState.pstsVariant = 'dark';
          break;
      }

      if(sts.code!=='0000'){
        tState.text = sts.code;
        tState.variant = 'danger';
      }

      this.progressKey = random(0,10000);
    },

    eventAlarmHandler(data){
      let tid = data.tid;
      if(!this.events[tid]) return; // 이 지역 탱크가 아니면 무시

      let events = data.payload;
      this.events[tid] = {data: events, dt: data.dt };

      let instance = null;
      let iNm = `evt-${tid}`
      if( Array.isArray( this.$refs[iNm]) ) instance = this.$refs[iNm][0];
      else instance = this.$refs[iNm];

      instance?.setEvents( this.events[tid] );
    },

    async inventoryHandler(data){
      let tid = data.tid;
      if(!this.tankState[tid]) return; // 이 지역 탱크가 아니면 무시
      await this.getTankChart(tid);
    },

    /**
     * 탱크별 그래프 데이터 (Dashboard.getTankChart 와 동일 API)
     * @param tid
     * @returns {Promise<void>}
     */
    async getTankChart(tid=''){
      let instance;
      try{
        let query='';
        if(tid) query= `tid=`+tid;
        const r = await apiCall('get', `/api/graph/tank/24?${query}`);
        if(r.code===200){
          if(tid && r.result.tankHist[tid] ) {
            const nm = 'chart-'+tid;
            instance = Array.isArray(this.$refs[nm])?this.$refs[nm][0]:this.$refs[nm];
            instance?.setChartData( r.result.tankHist[tid] );
          }else{
            for( const tid in r.result.tankHist){
              const nm = 'chart-'+tid;
              instance = Array.isArray(this.$refs[nm])?this.$refs[nm][0]:this.$refs[nm];
              instance?.setChartData( r.result.tankHist[tid] );
            }
          }
        }

      }catch(err){
        console.error(err);
      }finally{
        instance = null;
      }
    }
  },
}
</script>
